/**
 * Phase 39 — the pure rules around a verdict source: which kinds a card may
 * take, the tolerant parse of the saved JSON (anything odd = today's drops),
 * the metric vocabulary and the tag kind a source brings, the sample
 * guard's metric, the range's validity and the words every sentence uses.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FLOW_FORMULA_METRIC_IDS } from "../../formula/catalog";
import { GATE_METRICS } from "../../gate-rules/gate-metrics";
import { hourRangeLabel, parseHourRangeKey } from "../hour-formula";
import { DROP_GEN_HOUR_DATE_RANGES } from "../hour-profile.types";
import {
  DROP_GEN_VERDICT_CARDS,
  DROPS_SOURCE,
  type DropGenSourceSampleMetric,
  type DropGenVerdictCard,
  type DropGenVerdictSource,
  type DropGenVerdictSourceKind,
  type DropGenVerdictSources,
} from "./verdict-source.types";

const GATE_IDS: readonly string[] = GATE_METRICS.map((m) => m.id);
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The kinds a card may judge on — the gateway card its gateway, the list cards their list. */
export function allowedSourceKinds(
  card: DropGenVerdictCard,
): readonly DropGenVerdictSourceKind[] {
  return card === "gatewayVerdict" ? ["drops", "gateway"] : ["drops", "list"];
}

export const isTrackerSource = (
  src: DropGenVerdictSource,
): src is { kind: "list" | "gateway"; range: string } => src.kind !== "drops";

/** Why a tracker source's range cannot be read; null when it can. */
export function sourceRangeError(range: unknown): string | null {
  if (typeof range !== "string" || !range) return "a date range is needed";
  const parsed = parseHourRangeKey(range);
  if ("preset" in parsed)
    return (DROP_GEN_HOUR_DATE_RANGES as readonly string[]).includes(
      parsed.preset,
    )
      ? null
      : `"${parsed.preset}" is not a date range the verdicts know`;
  const { from, to } = parsed.custom;
  if (!DAY.test(from) || !DAY.test(to))
    return "a custom range needs YYYY-MM-DD bounds";
  return from <= to ? null : "a custom range ends before it starts";
}

/** One saved source, tolerant: a kind the card may not take or a bad range falls back to today's drops. */
export function parseVerdictSource(
  raw: unknown,
  card: DropGenVerdictCard,
): DropGenVerdictSource {
  if (!raw || typeof raw !== "object") return DROPS_SOURCE;
  const r = raw as Record<string, unknown>;
  if (r.kind === "drops") return DROPS_SOURCE;
  if (
    (r.kind === "list" || r.kind === "gateway") &&
    allowedSourceKinds(card).includes(r.kind)
  )
    return sourceRangeError(r.range) === null
      ? { kind: r.kind, range: r.range as string }
      : DROPS_SOURCE;
  return DROPS_SOURCE;
}

/** The saved `sourcesJson` as the judges read it — every card present, drops when unsaid. */
export function parseVerdictSources(
  json: unknown,
): Required<DropGenVerdictSources> {
  const raw =
    json && typeof json === "object" ? (json as Record<string, unknown>) : {};
  return Object.fromEntries(
    DROP_GEN_VERDICT_CARDS.map((card) => [
      card,
      parseVerdictSource(raw[card], card),
    ]),
  ) as Required<DropGenVerdictSources>;
}

/** The DTO's refusals: an unknown card, a kind the card may not take, a missing or bad range. */
export function verdictSourcesProblems(json: unknown): string[] {
  if (json === null || json === undefined) return [];
  if (typeof json !== "object" || Array.isArray(json))
    return ["sources must be an object keyed by card"];
  const out: string[] = [];
  for (const [card, raw] of Object.entries(json as Record<string, unknown>)) {
    if (!(DROP_GEN_VERDICT_CARDS as readonly string[]).includes(card)) {
      out.push(`"${card}" is not a verdict card`);
      continue;
    }
    const r =
      raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
    const kinds = allowedSourceKinds(card as DropGenVerdictCard);
    if (!r || !kinds.includes(r.kind as DropGenVerdictSourceKind)) {
      out.push(`${card}: the source must be one of ${kinds.join(", ")}`);
      continue;
    }
    if (r.kind === "drops" && r.range !== undefined)
      out.push(`${card}: today's drops take no date range`);
    const rangeError = r.kind === "drops" ? null : sourceRangeError(r.range);
    if (rangeError) out.push(`${card}: ${rangeError}`);
  }
  return out;
}

/** The metric ids a source's conditions may read. */
export function sourceMetricIds(
  kind: DropGenVerdictSourceKind,
): readonly string[] {
  return kind === "drops" ? GATE_IDS : FLOW_FORMULA_METRIC_IDS;
}

export const sourceSampleMetric = (
  kind: DropGenVerdictSourceKind,
): DropGenSourceSampleMetric => (kind === "drops" ? "smsSent" : "uniqueVisits");

/** Which tags a source's conditions test: drop tags on drops, the item's own otherwise. */
export const sourceTagKind = (
  kind: DropGenVerdictSourceKind,
): "drops" | "lists" | "gateways" =>
  kind === "drops" ? "drops" : kind === "list" ? "lists" : "gateways";

/** "today's drops to the list" / "the list's stats over the last 7 days". */
export function sourceWords(
  src: DropGenVerdictSource,
  noun: "list" | "gateway" | "drop",
): string {
  if (src.kind === "drops") {
    if (noun === "drop") return "each drop's own stats";
    return noun === "gateway"
      ? "today's drops through the gateway"
      : "today's drops to the list";
  }
  return `the ${src.kind}'s stats over ${hourRangeLabel(src.range)}`;
}

/** The same source twice — the editor's dirty compare and the reader's memo key. */
export const sourceKey = (src: DropGenVerdictSource): string =>
  src.kind === "drops" ? "drops" : `${src.kind}:${src.range}`;
